import LumeAvatar from "./LumeAvatar";
import Sidebar from "./Sidebar";
import ThemeToggle from "./ThemeToggle";
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ArrowLeftRight, CalendarClock, MessageSquareShare, Settings,
  Bell, Target, Landmark, CreditCard, BarChart3, Menu, X, LogOut, Plus, ArrowUpRight, ArrowDownRight, ShieldCheck, Scale, FileText,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import EmailVerificationBanner from "./EmailVerificationBanner";

const mobilePrimaryItems = [
  { to: "/dashboard", label: "Início", icon: LayoutDashboard },
  { to: "/transactions", label: "Lançamentos", icon: ArrowLeftRight },
  { to: "/accounts", label: "Bancos", icon: Landmark },
  { to: "/assistant", label: "LUME", icon: MessageSquareShare },
];

const allItems = [
  { to: "/dashboard", label: "Visão Geral", icon: LayoutDashboard },
  { to: "/transactions", label: "Lançamentos", icon: ArrowLeftRight },
  { to: "/planning", label: "Planejamentos", icon: Target },
  { to: "/bills", label: "Pagar e Receber", icon: CalendarClock },
  { to: "/cards", label: "Cartões", icon: CreditCard },
  { to: "/accounts", label: "Contas Bancárias", icon: Landmark },
  { to: "/reconciliation", label: "Conferir Caixa e Bancos", icon: Scale },
  { to: "/reports", label: "Relatórios", icon: BarChart3 },
  { to: "/notas-fiscais", label: "Notas Fiscais", icon: FileText, module: "fiscal_invoices" },
  { to: "/assistant", label: "Assistente LUME", icon: MessageSquareShare },
  { to: "/alerts", label: "Alertas", icon: Bell },
  { to: "/settings", label: "Configurações", icon: Settings },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, workspace, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const closeOnEscape = (event) => { if (event.key === "Escape") setSidebarOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [sidebarOpen]);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key.toLowerCase() !== "n" || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target?.isContentEditable) return;
      event.preventDefault();
      navigate("/transactions?new=EXPENSE");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  const startTransaction = (type) => {
    setQuickOpen(false);
    navigate(`/transactions?new=${type}`);
  };

  return (
    <div className="app-shell flex min-h-screen bg-night">
      <button
        type="button"
        onClick={() => setSidebarOpen((value) => !value)}
        className="hidden md:flex fixed top-4 left-4 z-[60] h-11 items-center gap-2 rounded-xl border border-white/10 bg-surface-subtle px-3 text-sm font-medium text-white/80 shadow-lg hover:text-white transition-colors"
        aria-expanded={sidebarOpen}
        aria-controls="app-sidebar"
        data-testid="sidebar-toggle"
      >
        {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
        <span>{sidebarOpen ? "Fechar menu" : "Menu"}</span>
      </button>
      {sidebarOpen && <button type="button" className="hidden md:block fixed inset-0 z-40 bg-black/50" aria-label="Fechar menu" tabIndex={-1} onClick={() => setSidebarOpen(false)} />}
      <Sidebar open={sidebarOpen} />
      <main className="flex-1 min-w-0 p-4 md:p-8 md:pt-20 pb-28 md:pb-8 max-w-full overflow-x-hidden">
        <EmailVerificationBanner />
        {children}
      </main>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu do sistema">
          <button type="button" className="absolute inset-0 bg-black/65 backdrop-blur-sm" aria-label="Fechar menu" onClick={() => setMenuOpen(false)} />
          <section className="absolute inset-x-0 bottom-0 max-h-[92dvh] overflow-y-auto rounded-t-[28px] border-t border-white/10 bg-surface-subtle px-4 pt-4 pb-[calc(92px+env(safe-area-inset-bottom))] shadow-2xl">
            <div className="flex items-center justify-between gap-3 px-1 mb-4">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-cyan">Navegação</div>
                <div className="font-semibold truncate">{workspace?.name || user?.name}</div>
              </div>
              <button type="button" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-muted" onClick={() => setMenuOpen(false)} aria-label="Fechar menu"><X size={19} /></button>
            </div>
            <nav className="grid grid-cols-2 gap-2" aria-label="Todas as áreas do sistema">
              {[...allItems.filter((item) => !item.module || workspace?.modules?.[item.module]?.enabled), ...(user?.is_platform_admin ? [{ to: "/admin", label: "Administração", icon: ShieldCheck }] : [])].map((item) => {
                const Icon = item.icon;
                const active = location.pathname.startsWith(item.to);
                return (
                  <NavLink key={item.to} to={item.to} className={`min-h-16 rounded-2xl border px-3 py-3 flex items-center gap-3 text-sm font-medium ${active ? "border-[rgba(0,229,255,.35)] bg-[rgba(0,229,255,.10)] text-cyan" : "border-white/[.07] bg-white/[.025] text-white/75"}`}>
                    {item.to === "/assistant" ? <LumeAvatar size={30} /> : <span className="w-9 h-9 rounded-xl bg-white/[.05] flex items-center justify-center shrink-0"><Icon size={18} /></span>}
                    <span className="leading-tight">{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
            <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
              <ThemeToggle inline />
              <button type="button" onClick={logout} className="min-h-12 rounded-2xl border border-white/[.07] px-4 flex items-center gap-2 text-sm text-danger"><LogOut size={17} /> Sair</button>
            </div>
          </section>
        </div>
      )}

      <div className="quick-transaction-launcher flex flex-col items-end gap-2">
        {quickOpen && <div className="flex flex-col items-stretch gap-2 mb-1" role="menu" aria-label="Novo lançamento">
          <button type="button" onClick={() => startTransaction("EXPENSE")} className="lumine-btn shadow-xl flex items-center gap-2 justify-start"><ArrowDownRight size={16} /> Despesa</button>
          <button type="button" onClick={() => startTransaction("INCOME")} className="lumine-btn shadow-xl flex items-center gap-2 justify-start"><ArrowUpRight size={16} /> Receita</button>
          <button type="button" onClick={() => startTransaction("TRANSFER")} className="lumine-btn shadow-xl flex items-center gap-2 justify-start"><ArrowLeftRight size={16} /> Transferência</button>
          <button type="button" onClick={() => { setQuickOpen(false); navigate("/cards?nova-compra=1"); }} className="lumine-btn shadow-xl flex items-center gap-2 justify-start" data-testid="quick-card-purchase"><CreditCard size={16} /> Compra no cartão</button>
        </div>}
        <button type="button" onClick={() => setQuickOpen((value) => !value)} className="quick-transaction-fab flex items-center justify-center" aria-label={quickOpen ? "Fechar atalhos de lançamento" : "Novo lançamento"} title="Novo lançamento" aria-expanded={quickOpen}><Plus size={26} className={quickOpen ? "rotate-45 transition-transform" : "transition-transform"} /></button>
      </div>

      <nav className="mobile-bottom-nav md:hidden" aria-label="Navegação principal">
        {mobilePrimaryItems.map((item) => {
          const Icon = item.icon;
          const active = location.pathname.startsWith(item.to);
          return (
            <NavLink key={item.to} to={item.to} className={`mobile-bottom-nav-item ${active ? "active" : ""}`}>
              {item.to === "/assistant" ? <LumeAvatar size={27} /> : <Icon size={20} />}
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <button type="button" onClick={() => setMenuOpen(true)} className={`mobile-bottom-nav-item ${menuOpen ? "active" : ""}`} aria-expanded={menuOpen} aria-haspopup="dialog">
          <Menu size={21} />
          <span>Menu</span>
        </button>
      </nav>
    </div>
  );
}
